import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { analysisPostDefinition } from '../../../../../entity-definitions/analysis-post.definition';
import { manualMeasureDefinition } from '../../../../../entity-definitions/manual-measure.definition';
import { automaticMeasureDefinition } from '../../../../../entity-definitions/automatic-measure.definition';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { MeasureDetailService } from '../../../../../services/measure-detail.service';
import { isEntityFormDeleteAction } from '../../../../../types/entity-form-result.interface';
import { AnalysisPost, ManualMeasure, AutomaticMeasure } from '../../../../../types/measure.interface';
import { CesButtonSecondaryComponent } from '../../../../ui/ces-buttons';
import { CesProperty } from '../../../../ui/ces-property/ces-property.component';
import { formatUnitLabel, mapUnitsToEntityOptions } from '../../../../../utils/unit-select.utils';

type EditableMeasure = ManualMeasure | AutomaticMeasure | AnalysisPost;

@Component({
  selector: 'app-measure-info',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TagModule, ChipModule, CesProperty, CesButtonSecondaryComponent, TranslocoModule, TooltipModule],
  templateUrl: './measure-info.component.html',
})
export class MeasureInfoComponent {
  protected readonly dataService = inject(MeasureDetailService);
  private readonly dialogService = inject(EntityDialogService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly measure = this.dataService.measure;
  protected readonly measureType = this.dataService.measureType;
  protected readonly unit = this.dataService.unit;
  protected readonly formatUnitLabel = formatUnitLabel;
  protected readonly kind = this.dataService.kind;

  protected readonly automaticMeasure = computed(() => {
    return this.kind() === 'automatic' ? (this.measure() as AutomaticMeasure | null) : null;
  });

  protected readonly locations = computed(() => this.dataService.locations().map(loc => loc.name));

  private readonly weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  protected dayOfWeekKey(day: number | null | undefined): string {
    return day != null && day >= 1 && day <= 7 ? (this.weekdayKeys[day - 1] ?? '') : '';
  }

  editMeasure(): void {
    const measure = this.measure();
    const kind = this.kind();
    if (!measure || !kind) return;

    const measureLocations = this.dataService.measureLocations();
    const definition =
      kind === 'analysis-post' ? analysisPostDefinition : kind === 'automatic' ? automaticMeasureDefinition : manualMeasureDefinition;

    this.dialogService
      .openEditDialog<EditableMeasure>(measure, {
        header: definition.messages.dialogTitles.edit(),
        headerParams: { name: measure.name },
        entityDefinition: definition,
        mode: 'edit',
        additionalData: {
          entityIds: measureLocations.map(loc => loc.locationId),
          Location: this.dataService.locationStore.entities(),
          MeasureType: this.dataService.measureTypeStore.entities(),
          Unit: mapUnitsToEntityOptions(this.dataService.unitStore.entities()),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result || isEntityFormDeleteAction(result)) return;

        if (Array.isArray(result['locationIds'])) {
          const resultLocationIds = result['locationIds'] as number[];
          const removed = measureLocations.filter(ml => !resultLocationIds.includes(ml.locationId));
          const added = resultLocationIds.filter(locationId => !measureLocations.some(ml => ml.locationId === locationId));

          added.forEach(locationId => {
            void this.dataService.measureLocationStore.create({ measureId: measure.id, locationId, name: '' });
          });
          removed.forEach(ml => {
            void this.dataService.measureLocationStore.delete(ml.id);
          });
        }

        void this.dataService.updateMeasure(result);
      });
  }
}
